import { isIntegration, isProduction } from '@client/components/auth';
import { utmParams } from '@services/utms';
import {
  registerTransport,
  track as coreTrack,
  reset as coreReset,
  getTransports,
} from './meta-pixel.core';
import { createConsoleTransport, createMetaPixelTransport } from './transports';
import { MetaPixelPayload, Transport } from './meta-pixel.types';

const META_PIXEL_ID = '666993466773487';

const sourceURLStorageKey = 'registration-sourceURL';
const forceMetaPixelStorageKey = 'alwaysUseMetaPixel';
const metaPixelIdStorageKey = 'metaPixelId';
const leadTrackedStorageKey = 'metaPixelLeadTracked';

let _initialized = false;

const readLocalStorageValue = (key: string): string => {
  try {
    return localStorage.getItem(key) ?? '';
  } catch (error) {
    return '';
  }
};

const readSessionStorageValue = (key: string): string => {
  try {
    return sessionStorage.getItem(key) ?? '';
  } catch (error) {
    return '';
  }
};

const writeSessionStorageValue = (key: string, value: string): void => {
  try {
    sessionStorage.setItem(key, value);
  } catch (error) {
    // Ignore storage errors (e.g., disabled cookies).
  }
};

const isLocalhost = (): boolean => {
  try {
    const { hostname } = window.location;
    return hostname === 'localhost' || hostname === '127.0.0.1';
  } catch (error) {
    return false;
  }
};

const shouldUseConsoleTransport = (): boolean => {
  if (isProduction()) return false;
  return isIntegration() || isLocalhost();
};

const shouldUseMetaPixelTransport = (pixelId: string): boolean => {
  if (!pixelId) return false;
  if (isProduction()) return true;
  return readLocalStorageValue(forceMetaPixelStorageKey) === 'true';
};

const resolvePixelId = (): string => {
  const override = readLocalStorageValue(metaPixelIdStorageKey);
  if (override) return override;
  return META_PIXEL_ID;
};

const hasTrackedLead = (): boolean =>
  readSessionStorageValue(leadTrackedStorageKey) === 'true';

const markLeadTracked = (): void =>
  writeSessionStorageValue(leadTrackedStorageKey, 'true');

const ensureInitialized = (): void => {
  if (_initialized) return;
  initMetaPixelTracking();
};

export const initMetaPixelTracking = (): void => {
  if (_initialized) return;

  const pixelId = resolvePixelId();
  if (shouldUseMetaPixelTransport(pixelId)) {
    registerTransport(createMetaPixelTransport(), {
      type: 'metapixel',
      pixelId,
    });
  } else if (shouldUseConsoleTransport()) {
    registerTransport(createConsoleTransport(), {
      type: 'console',
      enabled: true,
      prefix: '[Meta Pixel]',
    });
  }

  _initialized = true;
};

export const getAttributionPayload = (): MetaPixelPayload => {
  const utms = utmParams.restore();
  const sourceURL = tryReadSourceURL();

  return {
    ...utms,
    ...(sourceURL && { sourceURL }),
  };
};

export const trackLead = (payload?: MetaPixelPayload): void => {
  ensureInitialized();
  if (hasTrackedLead()) return;
  markLeadTracked();

  const eventPayload = {
    ...getAttributionPayload(),
    ...(payload ?? {}),
  };

  coreTrack('Lead', eventPayload);
};

export const resetMetaPixelTracking = (): void => {
  coreReset();
  _initialized = false;
};

export const getMetaPixelTransports = (): readonly Transport[] =>
  getTransports();

export const tryStoreSourceURL = () => {
  if (!window.location.search) return;
  try {
    const params = new URLSearchParams(window.location.search);
    const result = Object.fromEntries(params.entries());
    if (!result.sourceURL) return;
    localStorage.setItem(sourceURLStorageKey, result.sourceURL);
  } catch (error) {
    localStorage.removeItem(sourceURLStorageKey);
  }
};

export const tryReadSourceURL = () =>
  localStorage.getItem(sourceURLStorageKey) || '';

export const removeSourceURL = () =>
  localStorage.removeItem(sourceURLStorageKey);
